import { defineNuxtPlugin, useRouter, useNuxtApp, type NuxtApp } from 'nuxt/app';
import { nextTick } from 'vue';
import { focusAndScrollToElement, ID_MAIN, Navigation } from '~~/common/config/Navigation';
import { extractResourceFromPath } from '~~/common/utils/urlResource';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { FLUSH_VIEW_BANNER, FLUSH_VIEW_PRODUCT_LIST } from '~~/common/plugins/aw-analytics.js';
import { useAnalyticsProductListQueueStore } from '~~/common/stores/analyticsProductListQueue';

function createPluginProvides () {
  const nuxtApp = useNuxtApp();
  const pathAutofocusBlock = new Set<boolean>();
  const { $pinia, $logger, $localePath } = nuxtApp;
  const userInterfaceStore = useUserInterfaceStore($pinia);
  if (import.meta.client) {
    const router = useRouter();
    let isFirstPageLoad = true;
    // Automatic fragment focus
    // based on: node_modules/vue-router/dist/vue-router.esm.js
    router.afterEach(async (curr, prev) => {
      const isAutofocusBlocked = pathAutofocusBlock.has(true);
      pathAutofocusBlock.delete(true);
      const id = curr.hash.slice(1) || (!isFirstPageLoad && Navigation[ID_MAIN]);
      if (isFirstPageLoad) {
        isFirstPageLoad = false;
      }
      if (!id || ['search', 'category', 'product'].includes(extractResourceFromPath(curr.fullPath)?.resourceType ?? '')) {
        cleanAnalytics(nuxtApp);
        return;
      }
      let resolveNavigationPromise!: (element: HTMLElement | null) => void;
      const idNavigationPromise = new Promise<HTMLElement | null>((resolve) => {
        resolveNavigationPromise = resolve;
      });
      const e0 = document.getElementById(id);
      if (e0) {
        resolveNavigationPromise(e0);
      } else {
        if (!nuxtApp.isHydrating) {
          let r!: () => void;
          const p = new Promise<void>((resolve) => {
            r = resolve;
          });
          setTimeout(r, 2000);
          await p;
        }
        if (!nuxtApp.isHydrating) {
          return;
        }
        await nextTick();
        const e1 = document.getElementById(id);
        if (e1) {
          resolveNavigationPromise(e1);
        } else {
          try {
            if (!isAutofocusBlocked) {
              await router.options?.scrollBehavior?.call?.(router, curr, prev, null);
            }
            resolveNavigationPromise(document.getElementById(id));
          } catch (err) {
            $logger.error(err);
          }
        }
      }

      const navigationElement = await idNavigationPromise || document.getElementById(Navigation[ID_MAIN]);
      /* setTimeout is here so this runs later than the vue-router's
       * scrolling.
       * */
      setTimeout(() => {
        if (!userInterfaceStore.isBodyLocked && !isAutofocusBlocked && navigationElement) {
          focusAndScrollToElement(navigationElement);
        }
        cleanAnalytics(nuxtApp);
      }, 320);
    });
  }
  const resolvePath = (entity: string | { path: string } = { path: '/' }): string | { path: string } => {
    if (typeof entity === 'string') {
      return $localePath(entity);
    }

    /* TODO: Resource routing */
    return {
      path: $localePath('/'),
    };
  };

  return {
    path: resolvePath,
    pathAutofocusBlock,
  };
}

export type PathPluginProvides = ReturnType<typeof createPluginProvides>;

export default defineNuxtPlugin(() => {
  return {
    provide: createPluginProvides(),
  };
});

function cleanAnalytics (nuxtApp: NuxtApp) {
  setTimeout(() => {
    const { $awAnalytics, $logger, $pinia } = nuxtApp;
    const analyticsProductListQueueStore = useAnalyticsProductListQueueStore($pinia);
    try {
      $awAnalytics[FLUSH_VIEW_BANNER]({ isHistoryFlushed: true });
      $awAnalytics[FLUSH_VIEW_PRODUCT_LIST]();
      analyticsProductListQueueStore.clearViewedProductListHistory();
    } catch (err) {
      $logger.error(err);
    }
  }, 320);

}
